import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FleetSection } from '../../../../shared/sso-workspace/fleet-page'
import { FleetGrid, useGridList } from '../../../../shared/sso-workspace/fleet-grid'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatDateTime } from '../../../../shared/sso-workspace/format'
import { Drawer } from '../../../../shared/drawer/drawer'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { TableAgClient } from '../../../../shared/table-builder/table-ag-client'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../shared/menu/menu'
import { IconActivate, IconAdd, IconCancel, IconCheck, IconPermission, IconRefresh } from '../../../../shared/icon/icon'
import { activateCredential, createCredential, disableCredential, generateVapidCredential, listCredentials, rotateCredential, rotateFailure, testCredential } from '../../../../api-new/state/notify'
import { providerLabel } from '../../../../shared/notify/words'
import { CredentialForm } from './credential-form'
import { ReasonDialog } from './reason-dialog'
import { useNotifyOpsAccess } from './access'

const STATUS = {
  pending: { label: 'Waiting to be activated', tone: 'info' },
  active: { label: 'Active', tone: 'ok' },
  grace: { label: 'Grace', tone: 'warn' },
  disabled: { label: 'Disabled', tone: null }
}
const TEST = {
  ok: { label: 'Passed', tone: 'ok' },
  auth_failed: { label: 'Refused', tone: 'bad' },
  unreachable: { label: 'Unreachable', tone: 'warn' },
  invalid: { label: 'Invalid', tone: 'bad' }
}
const testLabel = r => (TEST[r] ? TEST[r].label : r || '')

const short = (v, n = 12) => (v && v.length > n ? `${v.slice(0, n)}…` : v || '')

// The metadata that identifies a credential (never its secret).
export const metadataText = c => {
  const m = (c && c.metadata) || {}
  if (c.provider === 'apns') return [m.keyId && `Key ${m.keyId}`, m.teamId && `Team ${m.teamId}`, (m.topics || []).join(', ')].filter(Boolean).join(' · ')
  if (c.provider === 'fcm') return [m.projectId, m.clientEmail].filter(Boolean).join(' · ')
  if (c.provider === 'webpush') return [short(m.publicKey), m.subject].filter(Boolean).join(' · ')
  return ''
}

const statusCell = row => {
  if (!row.data) return ''
  const s = STATUS[row.value] || { label: row.value }
  return (
    <span className="d-inline-flex flex-wrap gap-1 align-items-center">
      <Pill tone={s.tone}>{s.label}</Pill>
      {row.value === 'grace' && row.data.graceUntil && <span className="sw-hint small" title={formatDateTime(row.data.graceUntil)}>{`Until ${formatDateTime(row.data.graceUntil)}`}</span>}
    </span>
  )
}
const testCell = row => {
  if (!row.data) return ''
  if (!row.value) return <span className="sw-hint">Not tested</span>
  const t = TEST[row.value] || { label: row.value }
  return <span title={row.data.lastTestDetail || undefined}><Pill tone={t.tone}>{t.label}</Pill> <span className="sw-hint small">{formatAgo(row.data.lastTestedAt)}</span></span>
}
const metadataCell = row => (row.data ? <span className="sw-mono small">{metadataText(row.data)}</span> : '')
const providerCell = row => (row.data ? providerLabel(row.value) : '')
const envCell = row => (row.data ? (row.value === 'sandbox' ? 'Sandbox' : 'Production') : '')
const usedCell = row => (row.data && row.value ? <span title={formatDateTime(row.value)}>{formatAgo(row.value)}</span> : '')

// Activate a pending credential: the provider's active one goes to grace
// for graceHours (C-4 RotateOptions, 0-720, default 24).
const ActivateDialog = ({ credential, busy, error, failure, onConfirm, onCancel }) => {
  const [hours, setHours] = useState('24')
  useEffect(() => setHours('24'), [credential])
  useEffect(() => {
    if (!credential) return undefined
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); onCancel() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [credential, onCancel])
  if (!credential) return null
  const valid = Number.isInteger(Number(hours)) && Number(hours) >= 0 && Number(hours) <= 720
  return createPortal(
    <div className="sw-drawer justify-content-center align-items-center" onClick={e => { if (e.target === e.currentTarget) onCancel() }}>
      <div className="sw-dialog" role="alertdialog" aria-modal="true" aria-labelledby="nf-activate-title">
        <h2 className="h5" id="nf-activate-title">{`Activate ${credential.label}?`}</h2>
        <p className="sw-hint">{`It becomes the ${providerLabel(credential.provider)} credential in use. The one in use now keeps working for the grace period, then stops. This is recorded.`}</p>
        <label className="sw-label" htmlFor="nf-activate-grace">Grace period (hours)</label>
        <input id="nf-activate-grace" type="number" min={0} max={720} className="form-control mb-3" value={hours} onChange={e => setHours(e.target.value)} />
        {error && <p className="text-danger small" role="alert">{error}</p>}
        {error && <ErrorReference error={failure} className="mb-3" />}
        <div className="sw-dialog-actions">
          <button type="button" className="btn sw-btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn sw-btn-primary" disabled={busy || !valid} onClick={() => onConfirm(Number(hours))}>Activate</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// Push credentials (spec section 12, OD-18): the APNs key, the FCM service
// account and the VAPID key pair, sealed in the database by notify. The
// list shows metadata only; the secret is write-only (sent on add and
// rotate, never returned, never shown). Add makes a pending version; test
// it, then activate it (the previous one keeps working for a grace
// period). Rotate does add + test + activate in one step and leaves
// everything as it was when the test fails. Disable stops one at once.
// SYS_NOTIFY: read lists, update tests, create adds, activates and
// rotates, delete disables (C-6 section 4).
export const CredentialsTab = () => {
  const access = useNotifyOpsAccess()
  const [rows, setRows] = useState(undefined)
  const [loadError, loadFailure, setLoadError] = useErrorLine()
  const [reload, setReload] = useState(0)
  const [notice, setNotice] = useState(null)
  const [drawer, setDrawer] = useState(null) // { credential: null (add) | row (rotate) }
  const [formBusy, setFormBusy] = useState(false)
  const [formError, formFailure, setFormError] = useErrorLine()
  const [formTest, setFormTest] = useState(null) // { test, credentialId }: a rotate's failed test
  const [activating, setActivating] = useState(null)
  const [disabling, setDisabling] = useState(null)
  const [busy, setBusy] = useState(false)
  const [dialogError, dialogFailure, setDialogError] = useErrorLine()
  const [testing, setTesting] = useState(null)
  const tableRef = useRef()
  const live = useRef(true)

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  useEffect(() => {
    listCredentials().then(
      v => live.current && (setRows(Array.isArray(v) ? v : []), setLoadError(null)),
      err => live.current && (setRows(null), setLoadError(serverMessage(err, "Push credentials couldn't be loaded."), err))
    )
  }, [reload, setLoadError])

  const list = useGridList(rows)
  const refresh = () => setReload(n => n + 1)

  const runTest = useCallback(async c => {
    setTesting(c.id)
    setNotice(null)
    try {
      const r = await testCredential(c.id)
      if (!live.current) return
      setNotice({ tone: r && r.result === 'ok' ? 'ok' : 'bad', text: `${c.label}: ${testLabel(r && r.result)}${r && r.detail ? ` · ${r.detail}` : ''}` })
      refresh()
    } catch (err) {
      if (live.current) setNotice({ tone: 'bad', text: serverMessage(err, `${c.label} couldn't be tested.`), err })
    } finally {
      if (live.current) setTesting(null)
    }
  }, [])

  // Add: a pending version (Web Push generated: notify makes the pair).
  // Rotate: notify's one-step rotate (test, then activate, else 409 with
  // the test); a Web Push pair notify generates is generate -> test ->
  // activate here, the same steps, since a generated pair has no body.
  const submitForm = async (body, { generate = false } = {}) => {
    setFormBusy(true)
    setFormError(null)
    setFormTest(null)
    const rotating = drawer && drawer.credential
    try {
      let c
      if (!rotating) {
        c = generate ? await generateVapidCredential({ label: body.label, subject: body.subject }) : await createCredential(body)
      } else if (generate) {
        const fresh = await generateVapidCredential({ label: body.label, subject: body.subject })
        const test = await testCredential(fresh.id)
        if (!test || test.result !== 'ok') {
          if (!live.current) return
          setFormError('The new credential failed its test; it stays pending and the current one stays active.')
          setFormTest({ test: test || {}, credentialId: fresh.id })
          refresh()
          return
        }
        c = await activateCredential(fresh.id, body.graceHours)
      } else {
        c = await rotateCredential(rotating.id, body)
      }
      if (!live.current) return
      setDrawer(null)
      setNotice(rotating
        ? { tone: 'ok', text: `${(c && c.label) || body.label} version ${(c && c.version) || ''} is active. Version ${rotating.version} keeps working for ${body.graceHours} hours.` }
        : { tone: 'ok', text: `${(c && c.label) || body.label} added as version ${(c && c.version) || 1}, waiting to be activated. Test it, then activate it.` })
      refresh()
    } catch (err) {
      if (!live.current) return
      const failedTest = rotating ? rotateFailure(err) : null
      setFormError(serverMessage(err, rotating ? "The credential couldn't be rotated." : "The credential couldn't be added."), failedTest ? null : err)
      if (failedTest) {
        setFormTest(failedTest)
        refresh()
      }
    } finally {
      if (live.current) setFormBusy(false)
    }
  }

  const doActivate = async graceHours => {
    setBusy(true)
    setDialogError(null)
    try {
      await activateCredential(activating.id, graceHours)
      setNotice({ tone: 'ok', text: `${activating.label} is active.` })
      setActivating(null)
      refresh()
    } catch (err) {
      setDialogError(serverMessage(err, "The credential couldn't be activated."), err)
    } finally {
      setBusy(false)
    }
  }

  const doDisable = async reason => {
    setBusy(true)
    setDialogError(null)
    try {
      await disableCredential(disabling.id, reason)
      setNotice({ tone: 'ok', text: `${disabling.label} is disabled.` })
      setDisabling(null)
      refresh()
    } catch (err) {
      setDialogError(serverMessage(err, "The credential couldn't be disabled."), err)
    } finally {
      setBusy(false)
    }
  }

  const onlyActive = c => c && c.status === 'active' && (rows || []).filter(r => r.provider === c.provider && r.status === 'active').length === 1

  const rowMenu = useMemo(
    () => r =>
      <Menu small>
        <MenuItem Icon={IconCheck} text="test" pin show={access.canUpdate && r.status !== 'disabled'} disabled={testing === r.id} onClick={() => runTest(r)} />
        <MenuItem Icon={IconActivate} text="activate" pin show={access.canCreate && r.status === 'pending'} onClick={() => { setDialogError(null); setActivating(r) }} />
        <MenuItem Icon={IconRefresh} text="rotate" pin show={access.canCreate && r.status === 'active'} onClick={() => { setFormError(null); setFormTest(null); setDrawer({ credential: r }) }} />
        <MenuItem Icon={IconCancel} text="disable" pin show={access.canDelete && r.status !== 'disabled'} onClick={() => { setDialogError(null); setDisabling(r) }} />
      </Menu>,
    [access.canUpdate, access.canCreate, access.canDelete, testing, runTest, setDialogError, setFormError]
  )
  const headerMenu = useMemo(
    () => (access.canCreate ? [<MenuItem key="add" Icon={IconAdd} text="Add credential" pin show onClick={() => { setFormError(null); setFormTest(null); setDrawer({ credential: null }) }} />] : []),
    [access.canCreate, setFormError]
  )

  const rotating = drawer && drawer.credential

  return (
    <FleetSection id="nf-credentials" title="Push credentials"
      lede="The keys notify uses to reach Apple, Firebase and browsers. They are sealed on the server: after you add one, only its details are shown, never the key. Without an active credential, test environments capture pushes instead of sending them.">
      {loadError && <p className="text-danger small" role="alert">{loadError}</p>}
      {loadError && <ErrorReference error={loadFailure} className="mb-2" />}
      {notice && <p className={`small ${notice.tone === 'ok' ? 'text-success' : 'text-danger'}`} role="status">{notice.text}</p>}
      {notice && notice.err && <ErrorReference error={notice.err} className="mb-2" />}
      <FleetGrid title="Credentials" tableRef={tableRef} exportName="notify-credentials" menu={headerMenu} compact flush>
        <TableAgClient ref={tableRef} queryKey="credentials" apiList={list} sortCol="provider" pinFirstColumn
          emptyState={{ Icon: IconPermission, title: 'No push credentials', message: 'Add an APNs key, a Firebase service account or a VAPID key pair to send real push notifications.', action: { label: 'Add credential', onClick: () => setDrawer({ credential: null }), show: access.canCreate } }}>
          <ColumnText field="label" header="Label" sortable />
          <ColumnFormat field="provider" header="Provider" render={providerCell} exportValue={r => providerLabel(r.provider)} sortable />
          <ColumnFormat field="environment" header="Environment" render={envCell} sortable fit="content" />
          <ColumnFormat field="version" header="Version" render={r => (r.data ? `v${r.value}` : '')} sortable fit="content" />
          <ColumnFormat field="status" header="Status" render={statusCell} exportValue={r => (STATUS[r.status] || {}).label || r.status} sortable />
          <ColumnFormat field="metadata" header="Details" render={metadataCell} exportValue={metadataText} sortable={false} />
          <ColumnFormat field="lastTestResult" header="Last test" render={testCell} exportValue={r => testLabel(r.lastTestResult)} sortable />
          <ColumnFormat field="lastUsedAt" header="Last used" render={usedCell} sortable />
          <ColumnMenu menu={rowMenu} />
        </TableAgClient>
      </FleetGrid>

      {drawer &&
        <Drawer open size="md" title={rotating ? `Rotate ${rotating.label}` : 'Add a push credential'} onClose={() => setDrawer(null)}>
          {rotating && <p className="sw-hint">{`${providerLabel(rotating.provider)} · version ${rotating.version}. Upload the new key: it is tested, and only if the test passes does it replace this one.`}</p>}
          <CredentialForm key={rotating ? rotating.id : 'new'} credential={rotating || null} busy={formBusy} error={formError}
            errorReference={formError
              ? <>
                  {formTest &&
                    <div className="mb-2" role="status">
                      <p className="small m-0">{`Test: ${testLabel(formTest.test.result)}${formTest.test.detail ? ` · ${formTest.test.detail}` : ''}`}</p>
                      <p className="sw-hint small m-0">{`The new version${formTest.credentialId ? ` (credential ${formTest.credentialId})` : ''} stays pending: fix the key and rotate again, or disable it.`}</p>
                    </div>}
                  <ErrorReference error={formFailure} className="mb-2" />
                </>
              : null}
            onSubmit={submitForm} onCancel={() => setDrawer(null)} />
        </Drawer>}

      <ActivateDialog credential={activating} busy={busy} error={dialogError} failure={dialogFailure} onConfirm={doActivate} onCancel={() => setActivating(null)} />
      <ReasonDialog
        open={Boolean(disabling)}
        title={disabling ? `Disable ${disabling.label}?` : ''}
        body={disabling
          ? onlyActive(disabling)
            ? `This is the only active ${providerLabel(disabling.provider)} credential. On production that channel stops until another is activated; elsewhere pushes are captured instead. This is recorded.`
            : 'It stops being used at once and cannot be turned back on; add a new one instead. This is recorded.'
          : ''}
        confirmText="Disable"
        danger
        busy={busy}
        error={dialogError}
        failure={dialogFailure}
        onConfirm={doDisable}
        onCancel={() => setDisabling(null)}
      />
    </FleetSection>
  )
}
